import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";

const MOCK_HOLDINGS = [
  {
    symbol: "TSLA",
    description: "Tesla, Inc.",
    side: "Long",
    signalWeight: 0.1200,
    mktAdjWeight: 0.1500,
    returns: 0.1000,
  },
  {
    symbol: "MSFT",
    description: "Microsoft Corporation",
    side: "Long",
    signalWeight: 0.2000,
    mktAdjWeight: 0.1800,
    returns: -0.0500,
  },
  {
    symbol: "AAPL",
    description: "Apple Inc.",
    side: "Long",
    signalWeight: 0.0800,
    mktAdjWeight: 0.0900,
    returns: 0.0300,
  },
  {
    symbol: "AMZN",
    description: "Amazon.com, Inc.",
    side: "Short",
    signalWeight: -0.1500,
    mktAdjWeight: -0.1200,
    returns: -0.0800,
  },
  {
    symbol: "GOOGL",
    description: "Alphabet Inc.",
    side: "Long",
    signalWeight: 0.1800,
    mktAdjWeight: 0.2010,
    returns: 0.1500,
  },
  {
    symbol: "NVDA",
    description: "NVIDIA Corporation",
    side: "Long",
    signalWeight: 0.2200,
    mktAdjWeight: 0.2500,
    returns: 0.4500,
  },
  {
    symbol: "META",
    description: "Meta Platforms, Inc.",
    side: "Long",
    signalWeight: 0.1000,
    mktAdjWeight: 0.0801,
    returns: 0.2200,
  },
  {
    symbol: "NFLX",
    description: "Netflix, Inc.",
    side: "Short",
    signalWeight: -0.0800,
    mktAdjWeight: -0.1000,
    returns: -0.1200,
  },
  {
    symbol: "JPM",
    description: "JPMorgan Chase & Co.",
    side: "Long",
    signalWeight: 0.0500,
    mktAdjWeight: 0.0600,
    returns: 0.0700,
  },
  {
    symbol: "AMD",
    description: "Advanced Micro Devices, Inc.",
    side: "Short",
    signalWeight: -0.0600,
    mktAdjWeight: -0.0700,
    returns: -0.0300,
  },
];

// Column definitions — numeric: true drives the sort comparator
const COLUMNS = [
  { key: "symbol", label: "Symbol", align: "left", numeric: false },
  { key: "description", label: "Description", align: "left", numeric: false },
  { key: "side", label: "Side", align: "left", numeric: false },
  { key: "signalWeight", label: "Signal Weight", align: "right", numeric: true },
  { key: "mktAdjWeight", label: "Mkt Adj Weight", align: "right", numeric: true },
  { key: "returns", label: "Returns", align: "right", numeric: true },
];

const SignalSetHoldings = () => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState("symbol");
  const [order, setOrder] = useState("asc");

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  // Re-sort only when the active column or direction changes
  const sortedRows = useMemo(() => {
    return [...MOCK_HOLDINGS].sort((rowA, rowB) => {
      const valueA = rowA[orderBy];
      const valueB = rowB[orderBy];
      // String columns use locale-aware comparison - numeric columns use arithmetic diff
      if (typeof valueA === "string") {
        return order === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return order === "asc" ? valueA - valueB : valueB - valueA;
    });
  }, [orderBy, order]);

  const formatWeight = (value) => {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(4)}%`;
  };

  const formatReturns = (value) => {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(4)}%`;
  };

  // Green for positive returns, red for negative, neutral for zero/missing
  const getReturnsColor = (value) => {
    if (value === null || value === undefined) return theme.palette.text.primary;
    if (value > 0) return theme.palette.success.main;
    if (value < 0) return theme.palette.error.main;
    return theme.palette.text.primary;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "1.2rem" }}
        >
          Signal Set Holdings
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "40vh",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sortDirection={orderBy === column.key ? order : false}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === column.key}
                    direction={orderBy === column.key ? order : "asc"}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.symbol} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.side}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatWeight(row.signalWeight)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatWeight(row.mktAdjWeight)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 500, color: getReturnsColor(row.returns) }}
                >
                  {formatReturns(row.returns)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

  );
};

export default SignalSetHoldings;
